import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import * as THREE from "three";
import { MapControls } from "three/examples/jsm/controls/MapControls.js";

import { cn } from "@/lib/utils";
import { UserStatus } from "@/types/user-statuses.types";
import { getStatusCircleColor } from "@/utils/user-status";

import type { AgentsInformation } from "../..";
import { INCIDENT_OVERDUE_AFTER_MS, formatElapsed } from "./incident-time";
import { getSeatPositions } from "./seat-positions";
import {
    INCIDENT_ALERT_CLASS,
    INCIDENT_OVERDUE_CLASS,
    INCIDENT_TIMER_CLASS,
    SEAT_SELECTABLE_CLASS,
    SLACK_CONNECTED_CLASS,
    SLACK_NOT_CONNECTED_CLASS,
} from "./seat-tones";
import {
    type AgentProps,
    type CableProps,
    type LabelProps,
    OfficeView,
    type Point,
    type TableProps,
    type WallProps,
} from "./types";

// Plan area the camera frames on load and may pan within (flipped plan coordinates)
const VIEW_BOUNDS = { minX: -1100, maxX: 1090, minZ: -640, maxZ: 560 };
const MIN_ZOOM = 0.8;
const MAX_ZOOM = 3;
const CAMERA_FOV = 24; // Narrow field of view keeps walls and pillars close to upright
// Top view. Just short of 90° so lookAt keeps north up and MapControls keeps panning across the floor.
const CAMERA_ELEVATION = THREE.MathUtils.degToRad(89.5);

const WALL_HEIGHT: Record<WallProps["kind"], number> = { outer: 40, room: 28, pillar: 60 };
const WALL_THICKNESS: Record<WallProps["kind"], number> = { outer: 6, room: 3, pillar: 10 };
const TABLE_HEIGHT = 22;
const TABLE_TOP_THICKNESS = 4;
// Seat furniture in seat space at full scale: the status token sits at the table edge with a laptop in front of it.
// Each seat gets half of a 50 deep table, so token, laptop and the leaning screen must stay within 25 units of the
// edge or back-to-back laptops collide: 1.5 + 12 + 1.5 (token) + 6.5 (base) + ~1.9 (screen lean) ≈ 23.4.
const SEAT_RADIUS = 6;
const SEAT_HEIGHT = 4;
const SEAT_EDGE_GAP = 1.5;
const LAPTOP_WIDTH = 18;
const LAPTOP_DEPTH = 6.5;
const LAPTOP_THICKNESS = 1.2;
const LAPTOP_SCREEN_HEIGHT = 9;
const LAPTOP_SCREEN_TILT = THREE.MathUtils.degToRad(10);
const SELECTED_SEAT_SCALE = 1.25;
const CALL_RIPPLE_PERIOD = 2.4; // Seconds for one ripple to spread from an in-call seat and fade
const CALL_RIPPLE_SPREAD = 1.4; // How far the ripple grows beyond the token, relative to its radius
const CALL_SCREEN_BREATH_PERIOD = 3;
const CALL_ANIMATION_FPS = 30;
// Incident alert: a warning sign floating above the seat token, tilted towards the camera
const ALERT_SIZE = 26;
const ALERT_DEPTH = 2;
const ALERT_HEIGHT = 36; // Sign centre above the token
// Tilted to nearly face the camera, keeping a little depth so the sign still reads as 3D
const ALERT_TILT = CAMERA_ELEVATION - THREE.MathUtils.degToRad(30);
const ALERT_BOB_PERIOD = 1.8;
const ALERT_BOB_AMPLITUDE = 2;
const ALERT_SWAY = THREE.MathUtils.degToRad(12);
// Time badge above the sign: how long the incident has been open, red once it's overdue
const ALERT_BADGE_WIDTH = 40;
const ALERT_BADGE_HEIGHT = 13;
const ALERT_BADGE_PIXELS_PER_UNIT = 12;
const TEXT_RESOLUTION = 6; // Canvas pixels per plan unit for text textures
const CLICK_TOLERANCE_PX = 5;

const BLACK = new THREE.Color(0x000000);

export const getSeatKey = (tableId: string, seatNumber: number) => `${tableId}:${seatNumber}`;

export type SeatClickEvent = { tableId: string; agent: AgentProps };

export type OfficeSceneHandle = { resetView: () => void };

type OfficeSceneProps = {
    walls: WallProps[];
    // Building outline, in plan coordinates
    floor: Point[];
    labels: LabelProps[];
    tables: TableProps[];
    cables: CableProps[];
    // Keyed by getSeatKey(tableId, seatNumber)
    seatInformation: Record<string, AgentsInformation>;
    selectedSeatKey?: string;
    onSeatClick: (event: SeatClickEvent) => void;
    onZoomChange?: (zoom: number) => void;
    view?: OfficeView;
    // Users with a connected Slack account; undefined while unknown (seats stay neutral)
    slackConnectedUserIds?: ReadonlySet<string>;
    // User id → when their latest open incident was reported (epoch ms). Their seat shows a
    // floating alert with the time since then.
    incidentStartTimes?: ReadonlyMap<string, number>;
    // Picking mode: only these seats (getSeatKey) are clickable; the free ones among them are highlighted
    selectableSeatKeys?: ReadonlySet<string>;
    className?: string;
};

type Palette = {
    background: THREE.Color;
    floor: THREE.Color;
    primary: THREE.Color;
    mutedForeground: THREE.Color;
    card: THREE.Color;
    blue: THREE.Color;
    slackConnected: THREE.Color;
    slackNotConnected: THREE.Color;
    incidentAlert: THREE.Color;
    incidentTimer: THREE.Color;
    incidentOverdue: THREE.Color;
    seatSelectable: THREE.Color;
    unassigned: THREE.Color;
    status: Record<UserStatus, THREE.Color>;
    font: string;
};

type Seat = {
    key: string;
    tableId: string;
    seatNumber: number;
    x: number;
    y: number;
    scale: number;
    group: THREE.Group;
    token: THREE.Group;
    material: THREE.MeshStandardMaterial;
    screenMaterial: THREE.MeshStandardMaterial;
    ripple: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
    phase: number; // Offsets the in-call animation so neighbouring seats don't pulse in lockstep
    alert: THREE.Group;
    alertSign: THREE.Group;
    alertBadge?: AlertBadge; // Created the first time the seat shows an alert
};

type AlertBadge = {
    mesh: THREE.Mesh;
    context: CanvasRenderingContext2D;
    texture: THREE.CanvasTexture;
    text: string;
    overdue: boolean;
};

type SceneContext = {
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    controls: MapControls;
    requestRender: () => void;
    setAnimating: (animating: boolean) => void;
    resetView: () => void;
};

/** Resolve theme colors through Tailwind classes so the scene follows light/dark mode. */
const readPalette = (host: HTMLElement): Palette => {
    const probe = document.createElement("div");
    probe.style.display = "none";
    host.appendChild(probe);

    // Canvas normalises any CSS color syntax (oklch, hsl, ...) to sRGB bytes
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    const context = canvas.getContext("2d", { willReadFrequently: true })!;
    const toColor = (css: string) => {
        context.clearRect(0, 0, 1, 1);
        context.fillStyle = "#000";
        context.fillStyle = css;
        context.fillRect(0, 0, 1, 1);
        const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
        return new THREE.Color().setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace);
    };
    const read = (className: string, property: "color" | "backgroundColor") => {
        probe.className = className;
        return toColor(getComputedStyle(probe)[property]);
    };

    const status = Object.fromEntries(
        Object.values(UserStatus).map((value) => [value, read(getStatusCircleColor(value), "backgroundColor")])
    ) as Record<UserStatus, THREE.Color>;

    const palette: Palette = {
        background: read("bg-background", "backgroundColor"),
        floor: read("bg-muted", "backgroundColor"),
        primary: read("text-primary", "color"),
        mutedForeground: read("text-muted-foreground", "color"),
        card: read("bg-card", "backgroundColor"),
        blue: status[UserStatus.ACTIVE],
        slackConnected: read(SLACK_CONNECTED_CLASS, "backgroundColor"),
        slackNotConnected: read(SLACK_NOT_CONNECTED_CLASS, "backgroundColor"),
        incidentAlert: read(INCIDENT_ALERT_CLASS, "backgroundColor"),
        incidentTimer: read(INCIDENT_TIMER_CLASS, "backgroundColor"),
        incidentOverdue: read(INCIDENT_OVERDUE_CLASS, "backgroundColor"),
        seatSelectable: read(SEAT_SELECTABLE_CLASS, "backgroundColor"),
        unassigned: read(getStatusCircleColor(UserStatus.OFFLINE, false), "backgroundColor"),
        status,
        font: getComputedStyle(host).fontFamily,
    };

    probe.remove();
    return palette;
};

/** Flat text lying on the floor (or any horizontal surface). */
const createTextPlane = (
    text: string,
    {
        fontSize,
        color,
        font,
        opacity = 1,
        weight = 600,
    }: {
        fontSize: number;
        color: THREE.Color;
        font: string;
        opacity?: number;
        weight?: number;
    }
) => {
    const pixelSize = fontSize * TEXT_RESOLUTION;
    const fontSpec = `${weight} ${pixelSize}px ${font}`;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d")!;
    context.font = fontSpec;
    canvas.width = Math.ceil(context.measureText(text).width + pixelSize * 0.2);
    canvas.height = Math.ceil(pixelSize * 1.4);

    // Resizing the canvas resets its state
    context.font = fontSpec;
    context.fillStyle = `#${color.getHexString()}`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;

    const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(canvas.width / TEXT_RESOLUTION, canvas.height / TEXT_RESOLUTION),
        new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity, depthWrite: false })
    );
    mesh.rotation.x = -Math.PI / 2;
    return mesh;
};

const disposeObject = (object: THREE.Object3D) => {
    object.traverse((child) => {
        if (!(child instanceof THREE.Mesh || child instanceof THREE.Line)) return;
        child.geometry.dispose();
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        for (const material of materials) {
            (material as THREE.MeshBasicMaterial).map?.dispose();
            material.dispose();
        }
    });
};

const buildWall = (wall: WallProps, palette: Palette) => {
    const height = WALL_HEIGHT[wall.kind];
    const thickness = WALL_THICKNESS[wall.kind];
    const material = new THREE.MeshStandardMaterial({
        // Soften the theme color so walls frame the plan without overpowering the seats
        color: (wall.kind === "room" ? palette.mutedForeground : palette.primary)
            .clone()
            .lerp(palette.floor, wall.kind === "room" ? 0.45 : 0.25),
        roughness: 0.85,
    });
    const group = new THREE.Group();

    if (wall.kind === "pillar" || wall.points.length === 1) {
        const [point] = wall.points;
        const pillar = new THREE.Mesh(new THREE.CylinderGeometry(thickness, thickness, height, 24), material);
        pillar.position.set(point.x, height / 2, -point.y);
        group.add(pillar);
    } else {
        for (let i = 0; i < wall.points.length - 1; i++) {
            const from = wall.points[i];
            const to = wall.points[i + 1];
            const dx = to.x - from.x;
            const dz = -(to.y - from.y);
            const length = Math.hypot(dx, dz);
            if (length === 0) continue;

            // Extend each segment by its thickness so corners close cleanly
            const segment = new THREE.Mesh(new THREE.BoxGeometry(length + thickness, height, thickness), material);
            segment.position.set((from.x + to.x) / 2, height / 2, -(from.y + to.y) / 2);
            segment.rotation.y = Math.atan2(-dz, dx);
            group.add(segment);
        }
    }

    group.traverse((child) => {
        child.castShadow = true;
        child.receiveShadow = true;
    });
    return group;
};

const buildTable = (table: TableProps, palette: Palette) => {
    const group = new THREE.Group();
    const centerX = table.x + table.width / 2;
    const centerZ = -table.y + table.height / 2;

    const top = new THREE.Mesh(
        new THREE.BoxGeometry(table.width, TABLE_TOP_THICKNESS, table.height),
        new THREE.MeshStandardMaterial({ color: palette.card, roughness: 0.6 })
    );
    top.position.set(centerX, TABLE_HEIGHT - TABLE_TOP_THICKNESS / 2, centerZ);
    top.castShadow = true;
    top.receiveShadow = true;
    top.add(
        new THREE.LineSegments(
            new THREE.EdgesGeometry(top.geometry),
            new THREE.LineBasicMaterial({ color: palette.primary })
        )
    );
    group.add(top);

    const legMaterial = new THREE.MeshStandardMaterial({ color: palette.mutedForeground, roughness: 0.5 });
    const legHeight = TABLE_HEIGHT - TABLE_TOP_THICKNESS;
    const insetX = table.width / 2 - 5;
    const insetZ = table.height / 2 - 5;
    for (const [signX, signZ] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
    ]) {
        const leg = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.5, legHeight, 8), legMaterial);
        leg.position.set(centerX + signX * insetX, legHeight / 2, centerZ + signZ * insetZ);
        leg.castShadow = true;
        group.add(leg);
    }

    // Seats cover the tabletop, so the table name sits on the floor in front of it
    const label = createTextPlane(table.id, { fontSize: 14, color: palette.primary, font: palette.font });
    label.position.set(centerX, 0.4, -table.y + table.height + 12);
    group.add(label);

    return group;
};

/** Seat furniture is designed for a 50 deep table and shrinks on narrower ones. */
const getSeatScale = (table: TableProps) =>
    Math.min(1, (table.orientation === "horizontal" ? table.height : table.width) / 50);

/** Direction from the seat's table edge towards the middle of the table, i.e. the way the agent faces. */
const getSeatFacing = (table: TableProps, index: number) => {
    const sign = index % 2 === 0 ? 1 : -1;
    return table.orientation === "horizontal" ? { x: 0, z: sign } : { x: sign, z: 0 };
};

type SeatParts = {
    base: THREE.BufferGeometry;
    keyboard: THREE.BufferGeometry;
    lid: THREE.BufferGeometry;
    screen: THREE.BufferGeometry;
    bodyMaterial: THREE.Material;
    keyboardMaterial: THREE.Material;
    ripple: THREE.BufferGeometry;
    alertSign: THREE.BufferGeometry;
    alertStem: THREE.BufferGeometry;
    alertMaterial: THREE.Material;
    alertMark: THREE.Mesh;
};

/** Equilateral warning triangle with rounded corners, centred on its centroid. */
const createAlertSignGeometry = () => {
    const radius = ALERT_SIZE / Math.sqrt(3);
    const corner = 1.6;
    const points = [90, 210, 330].map((degrees) => {
        const angle = THREE.MathUtils.degToRad(degrees);
        return new THREE.Vector2(Math.cos(angle) * radius, Math.sin(angle) * radius);
    });
    const shape = new THREE.Shape();
    points.forEach((point, index) => {
        const previous = points[(index + 2) % 3];
        const next = points[(index + 1) % 3];
        const start = point.clone().lerp(previous, corner / ALERT_SIZE);
        const end = point.clone().lerp(next, corner / ALERT_SIZE);
        if (index === 0) shape.moveTo(start.x, start.y);
        else shape.lineTo(start.x, start.y);
        shape.quadraticCurveTo(point.x, point.y, end.x, end.y);
    });
    shape.closePath();

    return new THREE.ExtrudeGeometry(shape, {
        depth: ALERT_DEPTH,
        bevelEnabled: true,
        bevelThickness: 0.5,
        bevelSize: 0.5,
        bevelSegments: 2,
    }).translate(0, 0, -ALERT_DEPTH / 2);
};

const createSeatParts = (palette: Palette): SeatParts => ({
    base: new THREE.BoxGeometry(LAPTOP_WIDTH, LAPTOP_THICKNESS, LAPTOP_DEPTH),
    keyboard: new THREE.PlaneGeometry(LAPTOP_WIDTH - 3, LAPTOP_DEPTH * 0.55),
    lid: new THREE.BoxGeometry(LAPTOP_WIDTH, LAPTOP_SCREEN_HEIGHT, LAPTOP_THICKNESS * 0.6),
    screen: new THREE.PlaneGeometry(LAPTOP_WIDTH - 2, LAPTOP_SCREEN_HEIGHT - 2),
    bodyMaterial: new THREE.MeshStandardMaterial({ color: 0xc4c4cc, roughness: 0.35, metalness: 0.4 }),
    keyboardMaterial: new THREE.MeshStandardMaterial({ color: 0x27272a, roughness: 0.8 }),
    ripple: new THREE.RingGeometry(SEAT_RADIUS, SEAT_RADIUS + 1.6, 48).rotateX(-Math.PI / 2),
    alertSign: createAlertSignGeometry(),
    alertStem: new THREE.CylinderGeometry(0.5, 0.5, ALERT_HEIGHT, 8).translate(0, ALERT_HEIGHT / 2, 0),
    alertMaterial: new THREE.MeshStandardMaterial({
        color: palette.incidentAlert,
        emissive: palette.incidentAlert,
        emissiveIntensity: 0.25,
        roughness: 0.4,
    }),
    alertMark: (() => {
        const mark = createTextPlane("!", {
            fontSize: 19,
            color: new THREE.Color(0x1c1917),
            font: palette.font,
            weight: 800,
        });
        mark.rotation.x = 0; // Upright on the sign's face instead of lying flat
        return mark;
    })(),
});

/**
 * Alert in world space above a seat. The stem stays put while the sign bobs, and the sign
 * faces the (non-rotating) camera rather than the seat's own direction.
 */
const buildAlert = (parts: SeatParts) => {
    const sign = new THREE.Group();
    sign.position.y = ALERT_HEIGHT;
    sign.rotation.x = -ALERT_TILT;

    const plate = new THREE.Mesh(parts.alertSign, parts.alertMaterial);
    const mark = parts.alertMark.clone();
    // Sit on the front face, a little below the centroid where the triangle is widest
    mark.position.set(0, -1.6, ALERT_DEPTH / 2 + 0.6);
    sign.add(plate, mark);

    const alert = new THREE.Group();
    alert.add(new THREE.Mesh(parts.alertStem, parts.alertMaterial), sign);
    alert.visible = false;
    return { alert, sign };
};

const createAlertBadge = (): AlertBadge => {
    const canvas = document.createElement("canvas");
    canvas.width = ALERT_BADGE_WIDTH * ALERT_BADGE_PIXELS_PER_UNIT;
    canvas.height = ALERT_BADGE_HEIGHT * ALERT_BADGE_PIXELS_PER_UNIT;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;

    const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(ALERT_BADGE_WIDTH, ALERT_BADGE_HEIGHT),
        new THREE.MeshBasicMaterial({ map: texture, transparent: true })
    );
    // Just above the sign's top corner, on its front face
    mesh.position.set(0, ALERT_SIZE / Math.sqrt(3) + ALERT_BADGE_HEIGHT / 2 + 2, ALERT_DEPTH / 2 + 0.6);
    return { mesh, context: canvas.getContext("2d")!, texture, text: "", overdue: false };
};

const drawAlertBadge = (badge: AlertBadge, palette: Palette) => {
    const { canvas } = badge.context;
    const context = badge.context;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = `#${(badge.overdue ? palette.incidentOverdue : palette.incidentTimer).getHexString()}`;
    context.beginPath();
    context.roundRect(0, 0, canvas.width, canvas.height, canvas.height / 2);
    context.fill();

    context.fillStyle = "#ffffff";
    context.font = `700 ${Math.round(canvas.height * 0.62)}px ${palette.font}`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(badge.text, canvas.width / 2, canvas.height / 2 + 2);
    badge.texture.needsUpdate = true;
};

/**
 * Laptop in seat space: the agent sits at -z, so the keyboard is nearest to them and the
 * screen is hinged on the far side, tilted away from them and facing back towards them.
 */
const buildLaptop = (parts: SeatParts, screenMaterial: THREE.Material) => {
    const laptop = new THREE.Group();
    const baseStart = SEAT_RADIUS + SEAT_EDGE_GAP;

    const base = new THREE.Mesh(parts.base, parts.bodyMaterial);
    base.position.set(0, LAPTOP_THICKNESS / 2, baseStart + LAPTOP_DEPTH / 2);

    const keyboard = new THREE.Mesh(parts.keyboard, parts.keyboardMaterial);
    keyboard.rotation.x = -Math.PI / 2;
    keyboard.position.set(0, LAPTOP_THICKNESS + 0.05, baseStart + LAPTOP_DEPTH * 0.6);

    const hinge = new THREE.Group();
    hinge.position.set(0, LAPTOP_THICKNESS, baseStart + LAPTOP_DEPTH);
    hinge.rotation.x = LAPTOP_SCREEN_TILT;

    const lid = new THREE.Mesh(parts.lid, parts.bodyMaterial);
    lid.position.y = LAPTOP_SCREEN_HEIGHT / 2;

    const screen = new THREE.Mesh(parts.screen, screenMaterial);
    screen.rotation.y = Math.PI; // Face the agent
    screen.position.set(0, LAPTOP_SCREEN_HEIGHT / 2, -LAPTOP_THICKNESS * 0.3 - 0.05);

    hinge.add(lid, screen);
    laptop.add(base, keyboard, hinge);
    laptop.traverse((child) => {
        child.castShadow = true;
    });
    return laptop;
};

const buildSeats = (
    table: TableProps,
    palette: Palette,
    seatNumberPlanes: Map<number, THREE.Mesh>,
    seatParts: SeatParts
) => {
    const scale = getSeatScale(table);

    return getSeatPositions(table, (SEAT_RADIUS + SEAT_EDGE_GAP) * scale).map((position, index): Seat => {
        const seatNumber = index + 1;
        const material = new THREE.MeshStandardMaterial({ color: palette.unassigned, roughness: 0.45 });
        const chair = new THREE.Mesh(new THREE.CylinderGeometry(SEAT_RADIUS, SEAT_RADIUS, SEAT_HEIGHT, 32), material);
        chair.position.y = SEAT_HEIGHT / 2;
        chair.castShadow = true;

        let numberPlane = seatNumberPlanes.get(seatNumber);
        if (!numberPlane) {
            numberPlane = createTextPlane(String(seatNumber), {
                fontSize: SEAT_RADIUS,
                color: new THREE.Color(0xffffff),
                font: palette.font,
                weight: 700,
            });
            seatNumberPlanes.set(seatNumber, numberPlane);
        }
        // Seats turn towards the middle of the table, but the number stays upright for the camera
        const facing = getSeatFacing(table, index);
        const seatRotation = Math.atan2(facing.x, facing.z);
        const number = numberPlane.clone();
        number.position.y = SEAT_HEIGHT + 0.2;
        number.rotation.set(-Math.PI / 2, -seatRotation, 0, "YXZ");

        const token = new THREE.Group();
        token.add(chair, number);

        const screenMaterial = new THREE.MeshStandardMaterial({ color: 0x18181b, roughness: 0.2 });

        // Seats rest on the tabletop
        const group = new THREE.Group();
        group.position.set(position.x, TABLE_HEIGHT, position.y);
        group.rotation.y = seatRotation;
        group.scale.setScalar(scale);
        // Spreading ring shown while the agent is in a call
        const ripple = new THREE.Mesh(
            seatParts.ripple,
            new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
        );
        ripple.position.y = 0.3;
        ripple.visible = false;

        group.add(token, ripple, buildLaptop(seatParts, screenMaterial));

        // Kept out of the rotated seat group so it always faces the camera
        const { alert, sign: alertSign } = buildAlert(seatParts);
        alert.position.set(position.x, TABLE_HEIGHT + SEAT_HEIGHT * scale, position.y);
        alert.scale.setScalar(scale);

        const seat: Seat = {
            key: getSeatKey(table.id, seatNumber),
            tableId: table.id,
            seatNumber,
            x: position.x,
            y: position.y,
            scale,
            group,
            token,
            material,
            screenMaterial,
            ripple,
            phase: Math.random(),
            alert,
            alertSign,
        };
        group.userData.seat = seat;
        alert.userData.seat = seat;
        return seat;
    });
};

const buildCable = (cable: CableProps) => {
    const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(cable.points.map((point) => new THREE.Vector3(point.x, 1, -point.y))),
        new THREE.LineDashedMaterial({
            color: cable.color === "green" ? 0x10b981 : 0xf59e0b,
            dashSize: 5,
            gapSize: 5,
        })
    );
    line.computeLineDistances();
    return line;
};

const getBaseDistance = (aspect: number) => {
    const halfFov = THREE.MathUtils.degToRad(CAMERA_FOV) / 2;
    const width = VIEW_BOUNDS.maxX - VIEW_BOUNDS.minX;
    const depth = VIEW_BOUNDS.maxZ - VIEW_BOUNDS.minZ;
    const byWidth = width / 2 / (Math.tan(halfFov) * aspect);
    const byDepth = (depth * Math.sin(CAMERA_ELEVATION)) / 2 / Math.tan(halfFov);
    return Math.max(byWidth, byDepth);
};

export const OfficeScene = forwardRef<OfficeSceneHandle, OfficeSceneProps>(function OfficeScene(
    {
        walls,
        floor,
        labels,
        tables,
        cables,
        seatInformation,
        selectedSeatKey,
        onSeatClick,
        onZoomChange,
        view = OfficeView.CALL_STATUS,
        slackConnectedUserIds,
        incidentStartTimes,
        selectableSeatKeys,
        className,
    },
    ref
) {
    const containerRef = useRef<HTMLDivElement>(null);
    const contextRef = useRef<SceneContext | null>(null);
    const seatsRef = useRef<Seat[]>([]);
    const selectionRingRef = useRef<THREE.Mesh | null>(null);
    const paletteRef = useRef<Palette | null>(null);
    const hoveredSeatKeyRef = useRef<string | null>(null);
    const inCallSeatsRef = useRef<Seat[]>([]);

    // Latest props for imperative handlers
    const seatInformationRef = useRef(seatInformation);
    const selectedSeatKeyRef = useRef(selectedSeatKey);
    const viewRef = useRef(view);
    const slackConnectedUserIdsRef = useRef(slackConnectedUserIds);
    const incidentStartTimesRef = useRef(incidentStartTimes);
    const alertSeatsRef = useRef<Seat[]>([]);
    const selectableSeatKeysRef = useRef(selectableSeatKeys);
    const onSeatClickRef = useRef(onSeatClick);
    const onZoomChangeRef = useRef(onZoomChange);
    seatInformationRef.current = seatInformation;
    selectedSeatKeyRef.current = selectedSeatKey;
    viewRef.current = view;
    slackConnectedUserIdsRef.current = slackConnectedUserIds;
    incidentStartTimesRef.current = incidentStartTimes;
    selectableSeatKeysRef.current = selectableSeatKeys;
    onSeatClickRef.current = onSeatClick;
    onZoomChangeRef.current = onZoomChange;

    // Bumped when the app theme changes so the scene re-reads its colors
    const [themeVersion, setThemeVersion] = useState(0);

    useImperativeHandle(ref, () => ({ resetView: () => contextRef.current?.resetView() }), []);

    const getSeatColor = (palette: Palette, seatKey: string, information: AgentsInformation | undefined) => {
        const selectableSeatKeys = selectableSeatKeysRef.current;
        if (selectableSeatKeys) {
            if (information) return palette.status[UserStatus.OFFLINE];
            return selectableSeatKeys.has(seatKey) ? palette.seatSelectable : palette.unassigned;
        }
        if (!information) return palette.unassigned;
        if (viewRef.current === OfficeView.SLACK) {
            const connectedUserIds = slackConnectedUserIdsRef.current;
            if (!connectedUserIds) return palette.status[UserStatus.OFFLINE];
            return connectedUserIds.has(information.id) ? palette.slackConnected : palette.slackNotConnected;
        }
        return palette.status[information.status] ?? palette.unassigned;
    };

    /** Refresh the time on every shown alert; only redraws badges whose text or color changed. */
    const updateAlertBadges = () => {
        const palette = paletteRef.current;
        if (!palette) return;
        const now = Date.now();

        for (const seat of alertSeatsRef.current) {
            const userId = seatInformationRef.current[seat.key]?.id;
            const startedAt = userId ? incidentStartTimesRef.current?.get(userId) : undefined;
            if (startedAt === undefined) continue;

            if (!seat.alertBadge) {
                seat.alertBadge = createAlertBadge();
                seat.alertSign.add(seat.alertBadge.mesh);
            }
            const badge = seat.alertBadge;
            const text = formatElapsed(now - startedAt);
            const overdue = now - startedAt > INCIDENT_OVERDUE_AFTER_MS;
            if (text === badge.text && overdue === badge.overdue) continue;

            badge.text = text;
            badge.overdue = overdue;
            drawAlertBadge(badge, palette);
        }
    };
    const updateAlertBadgesRef = useRef(updateAlertBadges);
    updateAlertBadgesRef.current = updateAlertBadges;

    const applySeatState = () => {
        const palette = paletteRef.current;
        if (!palette) return;

        let selectedSeat: Seat | undefined;
        inCallSeatsRef.current = [];
        alertSeatsRef.current = [];
        for (const seat of seatsRef.current) {
            const information = seatInformationRef.current[seat.key];
            const color = getSeatColor(palette, seat.key, information);
            const isSelected = seat.key === selectedSeatKeyRef.current;
            const isHovered = seat.key === hoveredSeatKeyRef.current;

            seat.material.color.copy(color);
            seat.material.emissive.copy(color).multiplyScalar(isSelected || isHovered ? 0.35 : 0);
            seat.token.scale.setScalar(isSelected ? SELECTED_SEAT_SCALE : 1);

            // Occupied laptops glow in the agent's status color
            seat.screenMaterial.emissive.copy(information ? color : BLACK).multiplyScalar(0.8);
            seat.screenMaterial.emissiveIntensity = 1;

            // The call animation belongs to the call status view only, never to seat picking
            const isInCall =
                viewRef.current === OfficeView.CALL_STATUS &&
                !selectableSeatKeysRef.current &&
                information?.status === UserStatus.IN_CALL;
            seat.ripple.visible = isInCall;
            seat.ripple.material.color.copy(color);
            // Static halo; the animation loop takes over unless reduced motion is preferred
            seat.ripple.material.opacity = 0.35;
            seat.ripple.scale.setScalar(1.3);
            if (isInCall) inCallSeatsRef.current.push(seat);

            const hasAlert = !!information && !!incidentStartTimesRef.current?.has(information.id);
            seat.alert.visible = hasAlert;
            seat.alertSign.position.y = ALERT_HEIGHT;
            seat.alertSign.rotation.y = 0;
            if (hasAlert) alertSeatsRef.current.push(seat);
            if (isSelected) selectedSeat = seat;
        }

        const ring = selectionRingRef.current;
        if (ring) {
            ring.visible = !!selectedSeat;
            if (selectedSeat) {
                ring.position.set(selectedSeat.x, TABLE_HEIGHT + 0.3, selectedSeat.y);
                ring.scale.setScalar(selectedSeat.scale);
            }
        }
        if (contextRef.current) contextRef.current.renderer.shadowMap.needsUpdate = true;
        updateAlertBadges();
        contextRef.current?.setAnimating(inCallSeatsRef.current.length > 0 || alertSeatsRef.current.length > 0);
        contextRef.current?.requestRender();
    };

    // Renderer, camera and pan/zoom controls
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        // Shadows only change with the seats, not with panning or the in-call animation
        renderer.shadowMap.autoUpdate = false;
        renderer.domElement.style.display = "block";
        renderer.domElement.style.width = "100%";
        renderer.domElement.style.height = "100%";
        renderer.domElement.style.touchAction = "none";
        container.appendChild(renderer.domElement);

        const scene = new THREE.Scene();
        scene.add(new THREE.HemisphereLight(0xffffff, 0xb0b0b0, 1.6));
        const sun = new THREE.DirectionalLight(0xffffff, 1.2);
        sun.position.set(-500, 1200, 700);
        sun.castShadow = true;
        sun.shadow.mapSize.set(2048, 2048);
        Object.assign(sun.shadow.camera, { left: -1300, right: 1300, top: 900, bottom: -900, near: 100, far: 3000 });
        sun.shadow.bias = -0.0005;
        scene.add(sun);

        const camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, 10, 20000);
        const controls = new MapControls(camera, renderer.domElement);
        controls.enableRotate = false;
        controls.screenSpacePanning = false; // Pan across the floor, not the screen plane
        controls.zoomToCursor = true;
        controls.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
        controls.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_PAN };

        let baseDistance = getBaseDistance(1);
        const center = new THREE.Vector3(
            (VIEW_BOUNDS.minX + VIEW_BOUNDS.maxX) / 2,
            0,
            (VIEW_BOUNDS.minZ + VIEW_BOUNDS.maxZ) / 2
        );
        const panMin = new THREE.Vector3(VIEW_BOUNDS.minX, 0, VIEW_BOUNDS.minZ);
        const panMax = new THREE.Vector3(VIEW_BOUNDS.maxX, 0, VIEW_BOUNDS.maxZ);

        let frame = 0;
        const requestRender = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                renderer.render(scene, camera);
            });
        };

        const emitZoom = () => onZoomChangeRef.current?.(baseDistance / camera.position.distanceTo(controls.target));

        const updateDistanceLimits = () => {
            controls.minDistance = baseDistance / MAX_ZOOM;
            controls.maxDistance = baseDistance / MIN_ZOOM;
        };

        const resetView = () => {
            controls.target.copy(center);
            camera.position.set(
                center.x,
                baseDistance * Math.sin(CAMERA_ELEVATION),
                center.z + baseDistance * Math.cos(CAMERA_ELEVATION)
            );
            camera.lookAt(center);
            controls.update();
            emitZoom();
            requestRender();
        };

        controls.addEventListener("change", () => {
            // Keep the floor plan within reach while panning
            const clamped = controls.target.clone().clamp(panMin, panMax);
            const offset = clamped.sub(controls.target);
            if (offset.lengthSq() > 0) {
                controls.target.add(offset);
                camera.position.add(offset);
            }
            emitZoom();
            requestRender();
        });

        const resize = () => {
            const { clientWidth, clientHeight } = container;
            if (!clientWidth || !clientHeight) return;
            renderer.setSize(clientWidth, clientHeight, false);
            camera.aspect = clientWidth / clientHeight;
            camera.updateProjectionMatrix();
            baseDistance = getBaseDistance(camera.aspect);
            updateDistanceLimits();
            emitZoom();
            requestRender();
        };
        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(container);
        resize();
        resetView();

        // Seat picking: a click is a press and release without panning in between
        const raycaster = new THREE.Raycaster();
        const pointer = new THREE.Vector2();
        const pressStart = { x: 0, y: 0 };
        const pickSeat = (event: PointerEvent) => {
            const rect = renderer.domElement.getBoundingClientRect();
            pointer.set(
                ((event.clientX - rect.left) / rect.width) * 2 - 1,
                -((event.clientY - rect.top) / rect.height) * 2 + 1
            );
            raycaster.setFromCamera(pointer, camera);
            const selectableSeatKeys = selectableSeatKeysRef.current;
            const pickable = selectableSeatKeys
                ? seatsRef.current.filter((seat) => selectableSeatKeys.has(seat.key))
                : seatsRef.current;
            const targets = pickable.flatMap((seat) => (seat.alert.visible ? [seat.group, seat.alert] : [seat.group]));
            const hit = raycaster.intersectObjects(targets, true)[0]?.object;
            // Token, number, laptop and alert all lead back to their seat
            let object: THREE.Object3D | null | undefined = hit;
            while (object && !object.userData.seat) object = object.parent;
            return object?.userData.seat as Seat | undefined;
        };

        const handlePointerDown = (event: PointerEvent) => {
            pressStart.x = event.clientX;
            pressStart.y = event.clientY;
            renderer.domElement.style.cursor = "grabbing";
        };
        const handlePointerUp = (event: PointerEvent) => {
            const moved = Math.hypot(event.clientX - pressStart.x, event.clientY - pressStart.y);
            const seat = moved < CLICK_TOLERANCE_PX ? pickSeat(event) : undefined;
            renderer.domElement.style.cursor = seat ? "pointer" : "grab";
            if (!seat) return;

            const information = seatInformationRef.current[seat.key];
            onSeatClickRef.current({
                tableId: seat.tableId,
                agent: {
                    id: seat.seatNumber,
                    x: seat.x,
                    y: seat.y,
                    assigned: !!information?.id,
                    state: information?.status ?? UserStatus.OFFLINE,
                    userId: information?.id,
                },
            });
        };
        const handlePointerMove = (event: PointerEvent) => {
            if (event.buttons !== 0) return;
            const seatKey = pickSeat(event)?.key ?? null;
            renderer.domElement.style.cursor = seatKey ? "pointer" : "grab";
            if (seatKey === hoveredSeatKeyRef.current) return;
            hoveredSeatKeyRef.current = seatKey;
            applySeatState();
        };
        const handlePointerLeave = () => {
            if (!hoveredSeatKeyRef.current) return;
            hoveredSeatKeyRef.current = null;
            applySeatState();
        };

        renderer.domElement.style.cursor = "grab";
        renderer.domElement.addEventListener("pointerdown", handlePointerDown);
        renderer.domElement.addEventListener("pointerup", handlePointerUp);
        renderer.domElement.addEventListener("pointermove", handlePointerMove);
        renderer.domElement.addEventListener("pointerleave", handlePointerLeave);

        // In-call seats get a soft ripple and a breathing screen, and incident alerts bob gently.
        // The loop only runs while one of those is shown, and not at all for viewers who prefer
        // reduced motion.
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
        const timer = new THREE.Timer();
        let lastAnimationFrame = 0;
        const animate = (time: number) => {
            // A soft pulse doesn't need the display's full refresh rate
            // (with a little slack so frame timing jitter doesn't drop every other frame)
            if (time - lastAnimationFrame < 1000 / CALL_ANIMATION_FPS - 4) return;
            lastAnimationFrame = time;
            timer.update(time);
            const elapsed = timer.getElapsed();
            for (const seat of inCallSeatsRef.current) {
                const progress = (elapsed / CALL_RIPPLE_PERIOD + seat.phase) % 1;
                seat.ripple.scale.setScalar(1 + progress * CALL_RIPPLE_SPREAD);
                seat.ripple.material.opacity = 0.8 * (1 - progress) ** 2;

                const breath = Math.sin((elapsed / CALL_SCREEN_BREATH_PERIOD + seat.phase) * Math.PI * 2);
                seat.screenMaterial.emissiveIntensity = 1 + 0.35 * breath;
            }
            for (const seat of alertSeatsRef.current) {
                const wave = (elapsed / ALERT_BOB_PERIOD + seat.phase) * Math.PI * 2;
                seat.alertSign.position.y = ALERT_HEIGHT + Math.sin(wave) * ALERT_BOB_AMPLITUDE;
                seat.alertSign.rotation.y = Math.sin(wave / 2) * ALERT_SWAY;
            }
            renderer.render(scene, camera);
        };
        const setAnimating = (animating: boolean) => {
            renderer.setAnimationLoop(animating && !reducedMotion.matches ? animate : null);
        };

        // The alert times tick every second, also for viewers who prefer reduced motion
        const badgeTimer = window.setInterval(() => {
            if (alertSeatsRef.current.length === 0) return;
            updateAlertBadgesRef.current();
            requestRender();
        }, 1000);

        contextRef.current = { renderer, scene, camera, controls, requestRender, resetView, setAnimating };

        return () => {
            cancelAnimationFrame(frame);
            renderer.setAnimationLoop(null);
            window.clearInterval(badgeTimer);
            timer.dispose();
            resizeObserver.disconnect();
            renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
            renderer.domElement.removeEventListener("pointerup", handlePointerUp);
            renderer.domElement.removeEventListener("pointermove", handlePointerMove);
            renderer.domElement.removeEventListener("pointerleave", handlePointerLeave);
            controls.dispose();
            disposeObject(scene);
            renderer.dispose();
            renderer.domElement.remove();
            contextRef.current = null;
        };
    }, []);

    // Follow app theme switches
    useEffect(() => {
        const observer = new MutationObserver(() => setThemeVersion((version) => version + 1));
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "data-theme"] });
        return () => observer.disconnect();
    }, []);

    // Floor plan geometry
    useEffect(() => {
        const context = contextRef.current;
        const container = containerRef.current;
        if (!context || !container) return;

        const palette = readPalette(container);
        paletteRef.current = palette;
        context.scene.background = palette.background;

        const content = new THREE.Group();

        // Unlit so the surroundings match the page background exactly
        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(20000, 20000),
            new THREE.MeshBasicMaterial({ color: palette.background })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -0.5;
        content.add(ground);

        // Building floor follows the outline of the outer walls
        if (floor.length >= 3) {
            // Shape space is the plan's own x/y; lying it flat maps y to -z like everything else
            const shape = new THREE.Shape(floor.map((point) => new THREE.Vector2(point.x, point.y)));
            const floorMesh = new THREE.Mesh(
                new THREE.ShapeGeometry(shape).rotateX(-Math.PI / 2),
                new THREE.MeshStandardMaterial({ color: palette.floor, roughness: 1 })
            );
            floorMesh.receiveShadow = true;
            content.add(floorMesh);
        }

        for (const wall of walls) content.add(buildWall(wall, palette));
        for (const cable of cables) content.add(buildCable(cable));

        for (const label of labels) {
            const plane = createTextPlane(label.text, {
                fontSize: label.fontSize ?? 14,
                color: label.tint === "blue" ? palette.blue : palette.primary,
                font: palette.font,
                opacity: 0.35,
            });
            plane.position.set(label.x, 0.4, -label.y);
            content.add(plane);
        }

        const seatNumberPlanes = new Map<number, THREE.Mesh>();
        const seatParts = createSeatParts(palette);
        const seats: Seat[] = [];
        for (const table of tables) {
            content.add(buildTable(table, palette));
            for (const seat of buildSeats(table, palette, seatNumberPlanes, seatParts)) {
                seats.push(seat);
                content.add(seat.group, seat.alert);
            }
        }
        seatsRef.current = seats;

        const ring = new THREE.Mesh(
            new THREE.RingGeometry(SEAT_RADIUS * SELECTED_SEAT_SCALE + 1, SEAT_RADIUS * SELECTED_SEAT_SCALE + 2.5, 48),
            new THREE.MeshBasicMaterial({ color: palette.primary })
        );
        ring.rotation.x = -Math.PI / 2;
        ring.visible = false;
        selectionRingRef.current = ring;
        content.add(ring);

        context.scene.add(content);
        applySeatState();

        return () => {
            context.scene.remove(content);
            disposeObject(content);
            // Shared seat number templates are only cloned into the scene
            for (const plane of seatNumberPlanes.values()) disposeObject(plane);
            seatsRef.current = [];
            selectionRingRef.current = null;
        };
    }, [walls, floor, labels, tables, cables, themeVersion]);

    // Seat colors and selection
    useEffect(() => {
        applySeatState();
    }, [seatInformation, selectedSeatKey, view, slackConnectedUserIds, incidentStartTimes, selectableSeatKeys]);

    return <div ref={containerRef} className={cn("h-full w-full", className)} />;
});
